import React, {
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {T, useTranslate} from "@tolgee/react";
import FlexBox from "view/components/Layout/FlexBox";
import {
  IconButton,
  InputAdornment,
  OutlinedInputProps,
  Snackbar,
  TextField,
  Typography,
  useTheme,
} from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import {
  useDiscountCodeRemainingAmount,
  useDiscountCodeSnackbar,
  useShowDiscountCodes,
  useShowDiscountCodesActions,
} from "store/discount-code/discount-code-store";
import {useCurrentShowId, useDebouncedValue} from "hooks";
import {FullProduct} from "ts/api/product";
import {IStand, ISupplierCompany} from "@evennt-tech/api-v2-core";
import {apiV2} from "api/firebase";
import AppliedDiscountCode from "view/apps/Exhibitor/pages/Cart/components/DiscountCodes/Item";
import {
  parseResponseDiscountCode,
  validateDiscountCodeCategories,
} from "tools/discountCode";
import {
  DISCOUNT_CODE_ERROR_MESSAGE,
  DISCOUNT_CODE_SUCCESS_BASE_MESSAGE,
  DiscountCodeError,
} from "view/apps/Exhibitor/pages/Cart/components/DiscountCodes/helpers";
import {CurrencyContext} from "view/context/CurrencyContext";
import Alert from "view/components/Alert";
import {DISCOUNT_AMOUNT_UPDATED_MESSAGE} from "view/apps/Exhibitor/pages/Cart/components/DiscountCodes/constants";
import formatPrice from "tools/formats/formatPrice";
import useStyles from "./styles";

type Props = {
  stand: IStand;
  products: FullProduct[];
  categoryIdsBySupplier: Record<string, Set<string>>;
  showProducts: FullProduct[];
};

const CartDiscountCodes = ({
  stand,
  products,
  categoryIdsBySupplier,
  showProducts,
}: Props) => {
  const classes = useStyles();
  const {t} = useTranslate();
  const {palette} = useTheme();
  const showId = useCurrentShowId();
  const currency = useContext(CurrencyContext);

  const [debouncedInputValue, onChange] = useDebouncedValue("");

  const appliedDiscountCodes = useShowDiscountCodes(showId);
  const snackbar = useDiscountCodeSnackbar();
  const remainingAmount = useDiscountCodeRemainingAmount();
  const {
    append,
    enqueueSnackbar,
    hideSnackbar,
    updateError,
  } = useShowDiscountCodesActions();

  useEffect(() => {
    if (!snackbar) {
      return;
    }

    const timeout = setTimeout(
      () => hideSnackbar(snackbar.id),
      snackbar.message === DISCOUNT_AMOUNT_UPDATED_MESSAGE ? 7000 : 3000
    );

    return () => clearTimeout(timeout);
  }, [snackbar]);

  useEffect(() => {
    updateError(showId, categoryIdsBySupplier);
  }, [categoryIdsBySupplier]);

  const isValidInputValue = useMemo(
    () =>
      debouncedInputValue.length > 5 &&
      !appliedDiscountCodes.some(
        ({code}) => code.toLowerCase() === debouncedInputValue.toLowerCase()
      ),
    [debouncedInputValue, appliedDiscountCodes]
  );

  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState("");
  const uniqueSupplierCompanyById = useMemo(
    () =>
      showProducts.reduce<Record<string, ISupplierCompany>>((acc, {owner}) => {
        if (!acc[owner.id]) {
          acc[owner.id] = owner;
        }

        return acc;
      }, {}),
    [showProducts]
  );

  const onInputChange: OutlinedInputProps["onChange"] = ({target}) => {
    if (error) {
      setError("");
    }

    const trimmedValue = target.value.trim();
    setInputValue(trimmedValue);
    onChange(trimmedValue);
  };

  const renderSnackbar = (): ReactNode => {
    if (!snackbar) {
      return null;
    }

    return (
      <Snackbar open anchorOrigin={{vertical: "bottom", horizontal: "left"}}>
        <Alert
          severity={
            snackbar.message === DISCOUNT_AMOUNT_UPDATED_MESSAGE
              ? "info"
              : "success"
          }
          className={classes.snackbar}
        >
          <Typography variant="h4" className={classes.snackbarMessage}>
            {t(snackbar.message)}
          </Typography>
        </Alert>
      </Snackbar>
    );
  };

  const renderError = () => {
    if (!error) {
      return null;
    }

    const message =
      DISCOUNT_CODE_ERROR_MESSAGE[error as DiscountCodeError] ??
      "exhibitor.cart.discount.error.generic";

    return (
      <Alert fullWidth severity="error">
        <Typography variant="h4" className={classes.alertText}>
          {t(message)}
        </Typography>
      </Alert>
    );
  };

  const onClick = async () => {
    const uniqueSupplierCompanyIds = products.reduce<string[]>((acc, curr) => {
      if (!acc.includes(curr.owner.id)) {
        acc.push(curr.owner.id);
      }
      return acc;
    }, []);
    if (!uniqueSupplierCompanyIds.length || !debouncedInputValue) {
      return;
    }

    try {
      const discountCode = await apiV2.discountCode.validate({
        supplierCompanyIds: uniqueSupplierCompanyIds,
        standId: stand.id,
        discountCode: debouncedInputValue,
        showId,
      });
      if (!discountCode) {
        return setError(DiscountCodeError.NotFound);
      }
      const categoriesError = validateDiscountCodeCategories(
        discountCode.productCategories?.map((cat) => cat.productCategoryId),
        discountCode.supplierCompanyId,
        categoryIdsBySupplier
      );
      if (categoriesError) {
        return setError(categoriesError);
      }

      const parsedDiscountCode = parseResponseDiscountCode(discountCode);
      append(showId, parsedDiscountCode);

      enqueueSnackbar(DISCOUNT_CODE_SUCCESS_BASE_MESSAGE);
      setInputValue("");
      onChange("");
    } catch (e) {
      const errorMsg = e.response?.data?.message;
      if (typeof errorMsg === "string") {
        setError(errorMsg);
      }
      console.error("Error occurred redeeming discount code: ", e);
    }
  };

  const isBtnDisabled = !!error || !isValidInputValue || !products.length;

  return (
    <>
      {renderSnackbar()}
      <FlexBox column gap={2} className={classes.root}>
        <div className={classes.header}>
          <Typography variant="h5" color="primary" style={{fontWeight: 500}}>
            <T keyName="exhibitor.cart.discount.add_title" />
          </Typography>
        </div>
        <TextField
          className={classes.input}
          error={!!error}
          fullWidth
          label={t("exhibitor.cart.discount.code_label")}
          onChange={onInputChange}
          value={inputValue}
          variant="outlined"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  disabled={isBtnDisabled}
                  className={classes.icon}
                  onClick={onClick}
                >
                  <CheckIcon
                    htmlColor={
                      isBtnDisabled
                        ? palette.secondaryTextDisabled.main
                        : palette.primary.main
                    }
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {renderError()}
        {!!appliedDiscountCodes.length && !!products.length && (
          <FlexBox column gap={2} fullWidth>
            {appliedDiscountCodes.map((discountCode) => (
              <AppliedDiscountCode
                key={discountCode.id}
                discountCode={discountCode}
                supplierCompany={
                  uniqueSupplierCompanyById[discountCode.supplierCompanyId]
                }
              />
            ))}
          </FlexBox>
        )}
        {!!remainingAmount && (
          <Alert icon={false} severity="info">
            <T
              keyName="exhibitor.cart.discount.remaining_amount"
              params={{amount: formatPrice(remainingAmount, currency)}}
            />
            <br />
            <T keyName="exhibitor.cart.discount.add_more" />
          </Alert>
        )}
      </FlexBox>
    </>
  );
};

export default CartDiscountCodes;
